import { useEffect, useMemo, useState } from 'react';
import { get } from '@eika-infrastruktur/request';
import { GraphData, GraphDataApi, GraphDataResolution } from '../../../../../domain/Fund';
import { getTicks, mapApiToData } from '../savigProfile/graph/graphUtils';
import { SelectableProfile } from '../../../../../domain/Storebrand';

export type ExtendedGraphData = { data: GraphData; isin: string; name: string };

export type MultiGraphData = {
    graphDataSet: ExtendedGraphData[];
    pending: boolean;
    error: string | undefined;
    edgeDates: number[] | undefined;
    ticks: number[] | undefined;
    zeroAxisTicks: { x: number; y: number }[] | undefined;
};

// update it to 20 in 2029
export const estimatesYears = 15

const useHistoricGraphData = ({ isins, profiles }: { isins: string[]; profiles: SelectableProfile[] }): MultiGraphData => {
    const period = estimatesYears * 12;

    const [pending, setPending] = useState(true);
    const [error, setError] = useState<string | undefined>(undefined);

    const [graphDataSet, setGraphDataSet] = useState<ExtendedGraphData[]>([]);

    const edgeDates = useMemo(() => {
        if (!graphDataSet) return undefined;
        const edgePoints = graphDataSet.reduce(
            (minMax, graphData) => ({
                min: Math.min(minMax.min, graphData.data.series[0].points[0].x),
                max: Math.max(
                    minMax.max,
                    graphData.data.series[0].points[graphData.data.series[0].points.length - 1].x
                ),
            }),
            {
                min: new Date().getTime(),
                max: 0,
            }
        );
        return [edgePoints.min, edgePoints.max];
    }, [graphDataSet]);

    const edgeValues = useMemo(() => {
        if (!graphDataSet) return undefined;

        const getGraphDataMin = (data: GraphData) =>
            data.series[0].points.reduce((min, point) => Math.min(min, point.y), Infinity);

        const getGraphDataMax = (data: GraphData) =>
            data.series[0].points.reduce((max, point) => Math.max(max, point.y), -Infinity);

        return Object.values(graphDataSet).reduce(
            (minMax, graphData) => ({
                min: Math.min(minMax.min, getGraphDataMin(graphData.data)),
                max: Math.max(minMax.max, getGraphDataMax(graphData.data)),
            }),
            {
                min: Infinity,
                max: -Infinity,
            }
        );
    }, [graphDataSet]);
    const ticks = useMemo(() => getTicks(edgeValues), [edgeValues]);

    const zeroAxisTicks = edgeDates?.map((date) => ({ x: date, y: 10_000 }), [edgeDates]);

    useEffect(() => {
        if (isins.length !== graphDataSet?.length) {
            isins
                .filter((isin) => !graphDataSet?.find((graphData) => graphData.isin === isin))
                .forEach((isin) => {
                    get<GraphDataApi>(
                        `/sparing-min-side-web/rest/resource/fond/historisk/${isin}/${period}/${GraphDataResolution.MONTHLY}`
                    )
                        .then((apiData) => {
                            setGraphDataSet((state) => [
                                ...state.filter((graphData) => graphData.isin !== isin),
                                {
                                    data: mapApiToData(apiData),
                                    name: profiles.find((profile) => profile.isin === isin)?.name || '',
                                    isin,
                                },
                            ]);
                        })
                        .catch((error) => {
                            setError(error.message);
                            setPending(false);
                        });
                });
        }
    }, [isins]);

    useEffect(() => {
        if (isins.length && isins.length === graphDataSet?.length) {
            setError(undefined);
            setPending(false);
        }
    }, [isins, graphDataSet]);

    return { graphDataSet, pending, error, edgeDates, ticks, zeroAxisTicks };
};
export default useHistoricGraphData;
